import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import styles from "./Projects.module.css";

const cardVariants = {
  hidden: {
    opacity: 0,
    y: 24,
    scale: 0.97,
  },
  show: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.48,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

export default function ProjectCard({ project, index }) {
  const navigate = useNavigate();

  // Extract thumbnail: coverImage -> first gallery image -> description <img>
  const imgMatch = project.description?.match(/<img[^>]+src="([^">]+)"/);
  const thumbnail =
    project.coverImage ||
    (Array.isArray(project.images) && project.images.length > 0
      ? project.images[0]
      : imgMatch
      ? imgMatch[1]
      : null);

  // Strip HTML tags for clean 1-line plaque teaser
  const sourceText = project.overview || project.description || project.keySkills || "";
  const plainText = sourceText
    ? sourceText.replace(/<[^>]+>/g, " ").replace(/&nbsp;/g, " ").replace(/\s+/g, " ").trim()
    : "Exhibition showcase and design research notes.";

  const teaser =
    plainText.length > 110 ? `${plainText.slice(0, 110)}…` : plainText;

  const formattedDate = project.date
    ? new Date(project.date).toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "Curated Work";

  return (
    <motion.div
      variants={cardVariants}
      whileHover={{
        y: -4,
        boxShadow:
          "0 18px 40px -10px rgba(47, 137, 86, 0.22), 0 8px 18px -6px rgba(0,0,0,0.12)",
        transition: { duration: 0.3, ease: "easeOut" },
      }}
      className={styles.artworkFrame}
      onClick={() => navigate(`/projects/${project._id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          navigate(`/projects/${project._id}`);
        }
      }}
    >
      {/* Artwork Image Viewport */}
      <div className={styles.artworkImageWrap}>
        <div className={styles.artworkBadge}>#{index + 1}</div>
        {project.type && (
          <div className={styles.artworkTypeBadge}>{project.type}</div>
        )}

        {thumbnail ? (
          <img
            src={thumbnail}
            alt={project.title}
            loading="lazy"
            decoding="async"
            className={styles.artworkImage}
          />
        ) : (
          <div className={styles.artworkNoImage}>
            <span>🎨</span>
          </div>
        )}

        {/* Ambient gallery spotlight on hover */}
        <div className={styles.artworkSpotlight} />
      </div>

      {/* Exhibit Plaque (Museum Tag) */}
      <div className={styles.plaque}>
        <div className={styles.plaqueLine} />
        <h3 className={styles.plaqueTitle}>{project.title}</h3>
        <p className={styles.plaqueTeaser}>{teaser}</p>
        <p className={styles.plaqueDate}>Date: {formattedDate}</p>
      </div>
    </motion.div>
  );
}
